import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Drawer, Form, Input, Select, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { userService, userUnitService } from '../../services';
import './UserManagement.css';

const UserManagement = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [form] = Form.useForm();
    const [userUnits, setUserUnits] = useState([]);
    const [currentUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));

    useEffect(() => {
        fetchUsers();
        fetchUserUnits();
    }, [page, limit]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await userService.getUserList(page, limit);
            if (response.data.code === 200) {
                setData(response.data.data.data);
                setTotal(response.data.data.total);
            }
        } catch (error) {
            message.error('获取用户列表失败');
        } finally {
            setLoading(false);
        }
    };

    const fetchUserUnits = async () => {
        try {
            const response = await userUnitService.getAllUserUnits();
            if (response.data.code === 200) {
                setUserUnits(response.data.data);
            }
        } catch (error) {
            message.error('获取用户单位列表失败');
        }
    };

    const handleCreate = () => {
        setEditingUser(null);
        form.resetFields();
        // manager新建用户时自动赋值用户单位
        if (currentUser.role === 'manager') {
            form.setFieldsValue({ user_unit_id: currentUser.user_unit_id });
        }
        setDrawerVisible(true);
    };

    const handleEdit = (record) => {
        setEditingUser(record);
        form.setFieldsValue(record);
        setDrawerVisible(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('确定删除该用户吗？')) {
            try {
                const response = await userService.deleteUser(id);
                if (response.data.code === 200) {
                    message.success('删除成功');
                    fetchUsers();
                }
            } catch (error) {
                message.error(error.response?.data?.message || '删除失败');
            }
        }
    };

    const handleSubmit = async (values) => {
        try {
            // manager新建用户时，确保user_unit_id自动赋值
            let submitValues = { ...values };
            if (currentUser.role === 'manager' && !editingUser) {
                submitValues.user_unit_id = currentUser.user_unit_id;
            }
            if (editingUser) {
                const response = await userService.updateUser(editingUser.id, submitValues);
                if (response.data.code === 200) {
                    message.success('更新成功');
                }
            } else {
                const response = await userService.createUser(submitValues);
                if (response.data.code === 200) {
                    message.success('创建成功');
                }
            }
            setDrawerVisible(false);
            fetchUsers();
        } catch (error) {
            message.error(error.response?.data?.message || '操作失败');
        }
    };

    const columns = [
        {
            title: '用户名',
            dataIndex: 'username',
            key: 'username',
            width: 120
        },
        {
            title: '真实姓名',
            dataIndex: 'real_name',
            key: 'real_name',
            width: 120
        },
        {
            title: '角色',
            dataIndex: 'role',
            key: 'role',
            width: 120,
            render: (role) => {
                const roleMap = {
                    admin: '系统管理员',
                    manager: '管理员',
                    advanced_user: '高级用户',
                    user: '普通用户'
                };
                return roleMap[role] || role;
            }
        },
        {
            title: '用户单位',
            dataIndex: 'user_unit',
            key: 'user_unit',
            width: 120
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => (status === 1 ? '活跃' : '禁用')
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        编辑
                    </Button>
                    <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                        disabled={record.id === currentUser.id}
                    >
                        删除
                    </Button>
                </Space>
            )
        }
    ];

    const getRoleOptions = () => {
        if (currentUser.role === 'admin') {
            return [
                { label: '系统管理员', value: 'admin' },
                { label: '管理员', value: 'manager' },
                { label: '高级用户', value: 'advanced_user' },
                { label: '普通用户', value: 'user' }
            ];
        } else if (currentUser.role === 'manager') {
            return [
                { label: '高级用户', value: 'advanced_user' },
                { label: '普通用户', value: 'user' }
            ];
        }
        return [];
    };

    return (
        <div className="user-management">
            <Card title="用户管理" className="management-card">
                <Space style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                        新增用户
                    </Button>
                </Space>

                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: page,
                        pageSize: limit,
                        total,
                        onChange: (p) => setPage(p),
                        onShowSizeChange: (_, s) => setLimit(s)
                    }}
                />
            </Card>

            <Drawer
                title={editingUser ? '编辑用户' : '新增用户'}
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
                width={400}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    autoComplete="off"
                >
                    <Form.Item
                        label="用户名"
                        name="username"
                        rules={[{ required: true, message: '请输入用户名' }]}
                    >
                        <Input disabled={!!editingUser} autoComplete="off" />
                    </Form.Item>

                    {!editingUser && (
                        <Form.Item
                            label="密码"
                            name="password"
                            rules={[{ required: true, message: '请输入密码' }]}
                        >
                            <Input.Password autoComplete="new-password" />
                        </Form.Item>
                    )}

                    <Form.Item
                        label="真实姓名"
                        name="real_name"
                        rules={[{ required: true, message: '请输入真实姓名' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="角色"
                        name="role"
                        rules={[{ required: true, message: '请选择角色' }]}
                    >
                        <Select
                            options={getRoleOptions()}
                            disabled={editingUser && editingUser.id === currentUser.id}
                        />
                    </Form.Item>

                    {/* admin 可以选择用户单位，manager 自动使用自己的用户单位 */}
                    {currentUser.role === 'admin' && (
                        <Form.Item
                            label="用户单位"
                            name="user_unit_id"
                            rules={[{ required: true, message: '请选择用户单位' }]}
                        >
                            <Select
                                options={userUnits.map(unit => ({
                                    label: unit.name,
                                    value: unit.id
                                }))}
                                placeholder="请选择用户单位"
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        label="状态"
                        name="status"
                        initialValue={1}
                        rules={[{ required: true, message: '请选择状态' }]}
                    >
                        <Select options={[
                            { label: '活跃', value: 1 },
                            { label: '禁用', value: 0 }
                        ]} />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
                            {editingUser ? '更新' : '创建'}
                        </Button>
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
};

export default UserManagement;
